import {
  calculateDynamicDateRange,
  calculatePresetDateRange,
} from "@/utils/date/dateRange"
import type { DateRangePreset } from "@/utils/date/dateRangeTypes"

import type { GlobalDateRange } from "../types/core"
import type { DashboardDatePreferences } from "../types/dashboardConfig"

interface ResolveDashboardDateRangeParams {
  defaultPreset: DateRangePreset
  savedPreferences?: DashboardDatePreferences
  urlEnd?: string | null
  urlStart?: string | null
  calculateDynamicRange?: (
    amount: number,
    unit: "days" | "weeks" | "months",
  ) => GlobalDateRange
  calculatePresetRange?: (preset: DateRangePreset) => GlobalDateRange
}

interface BuildWidgetConfigHrefParams {
  currentTab?: string | null
  dashboardId: string
  organizationSlug: string
  widgetId?: string
  activeDateRange?: GlobalDateRange | null
}

interface BuildDashboardHrefParams {
  currentTab?: string | null
  dashboardId: string
  organizationSlug: string
  urlEnd?: string | null
  urlStart?: string | null
}

export const getDashboardDisplayName = (
  dashboard:
    | {
        display_name?: string | null
        is_default?: boolean
      }
    | null
    | undefined,
): string | undefined => {
  if (!dashboard) {
    return undefined
  }

  if (dashboard.is_default) {
    return "Main"
  }

  return dashboard.display_name?.trim() || "Untitled"
}

const parseUrlDateRange = (
  urlStart?: string | null,
  urlEnd?: string | null,
): GlobalDateRange | null => {
  if (!urlStart || !urlEnd) {
    return null
  }

  const start = new Date(urlStart)
  const end = new Date(urlEnd)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return null
  }

  return { start, end }
}

export const resolveDashboardDateRangeOverride = ({
  defaultPreset,
  savedPreferences,
  urlEnd,
  urlStart,
  calculateDynamicRange = calculateDynamicDateRange,
  calculatePresetRange = calculatePresetDateRange,
}: ResolveDashboardDateRangeParams): GlobalDateRange => {
  if (savedPreferences?.preset) {
    return calculatePresetRange(savedPreferences.preset)
  }

  if (savedPreferences?.relativeConfig) {
    return calculateDynamicRange(
      savedPreferences.relativeConfig.amount,
      savedPreferences.relativeConfig.unit,
    )
  }

  if (savedPreferences?.customRange) {
    return {
      start: new Date(savedPreferences.customRange.start),
      end: new Date(savedPreferences.customRange.end),
    }
  }

  return (
    parseUrlDateRange(urlStart, urlEnd) ?? calculatePresetRange(defaultPreset)
  )
}

export const buildDashboardWidgetConfigurationHref = ({
  currentTab,
  dashboardId,
  organizationSlug,
  widgetId,
  activeDateRange,
}: BuildWidgetConfigHrefParams): string => {
  const widgetSegment = widgetId ?? "new"
  const basePath = `/${organizationSlug}/dashboard/${dashboardId}/widgets/configure/${widgetSegment}`
  const searchParams = new URLSearchParams()

  if (activeDateRange) {
    searchParams.set("start", activeDateRange.start.toISOString())
    searchParams.set("end", activeDateRange.end.toISOString())
  }

  if (currentTab) {
    searchParams.set("tab", currentTab)
  }

  const queryString = searchParams.toString()
  return queryString ? `${basePath}?${queryString}` : basePath
}

export const buildDashboardHref = ({
  currentTab,
  dashboardId,
  organizationSlug,
  urlEnd,
  urlStart,
}: BuildDashboardHrefParams): string => {
  const basePath = `/${organizationSlug}/dashboard/${dashboardId}`
  const searchParams = new URLSearchParams()

  if (urlStart && urlEnd) {
    searchParams.set("start", urlStart)
    searchParams.set("end", urlEnd)
  }

  if (currentTab) {
    searchParams.set("tab", currentTab)
  }

  const queryString = searchParams.toString()
  return queryString ? `${basePath}?${queryString}` : basePath
}
